import { useEffect, useRef, useState } from 'react';
import { Maximize2, Minimize2, Send, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { MrBubbles, MrBubblesFull } from '@/components/tour/MrBubbles';
import { useBubblyOpenRequest } from './BubblyContext';
import { useSupportChat, type ChatMessage } from '@/hooks/useSupportChat';

/**
 * BubblyWidget — Intercom-style floating chat assistant.
 *
 * A fixed bottom-right launcher button (full-body Mr. Bubbles) toggles a chat panel wired
 * to the existing RAG support backend via `useSupportChat` (POST /kb-chat with
 * the customer's Supabase bearer). We deliberately REUSE that hook rather than
 * add a second chat backend call — Bubbly is a new persona/launcher over the
 * same engine.
 *
 * Mounted once in AppShell so it appears on every signed-in customer page.
 * Tagged `data-app-chrome` so it's hidden in the app's print CSS, and lifted
 * above the mobile BottomNav (which sits at z-40 / bottom-0).
 */

const EXAMPLE_QUESTIONS = [
  "What's my current balance?",
  "Where's my last order?",
  'How many tanks do I have?',
  'My soda has no fizz',
];

export function BubblyWidget() {
  const [open, setOpen] = useState(false);
  // Big centered window vs the compact docked panel. Persisted while the
  // panel stays open; toggled by the header maximize button or an
  // openBubbly({ expanded: true }) request (Resources page).
  const [expanded, setExpanded] = useState(false);
  const { messages, sending, send } = useSupportChat();
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);

  // External "Contact us / chat" entry points (Contact-us menu, Guide page)
  // open Bubbly via this counter rather than rendering a second chat panel.
  const openRequest = useBubblyOpenRequest();
  useEffect(() => {
    if (openRequest.id > 0) {
      setOpen(true);
      if (openRequest.expanded) setExpanded(true);
    }
  }, [openRequest.id, openRequest.expanded]);

  // Auto-scroll to newest message as the list grows / a reply lands.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  // When the panel opens: focus the input. Esc closes and returns focus to the
  // launcher.
  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), 50);
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        launcherRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function submit(text: string) {
    const value = text.trim();
    if (!value || sending) return;
    void send(value);
    setInput('');
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    submit(input);
  }

  function onInputKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit(input);
    }
  }

  const empty = messages.length === 0;

  return (
    <div data-app-chrome>
      {/* Launcher — fixed bottom-right. Lifted on mobile so it clears the
          56px BottomNav (which is md:hidden, bottom-0, z-40). */}
      <button
        ref={launcherRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={open ? 'Close Mr. Bubbles assistant' : 'Open Mr. Bubbles assistant'}
        className={cn(
          'fixed right-4 z-[70] flex h-16 w-16 items-center justify-center rounded-full',
          'bg-brix-primary shadow-card-hover ring-1 ring-white/10',
          'cursor-pointer transition-all duration-200 hover:scale-105 active:scale-95',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brix-primary/40 focus-visible:ring-offset-2',
          // Sit above the mobile bottom nav (h-14) + safe area; normal on desktop.
          'bottom-[calc(3.5rem+env(safe-area-inset-bottom)+0.75rem)] md:bottom-5',
          'sm:h-20 sm:w-20',
        )}
      >
        {open ? (
          <X size={24} className="text-white" />
        ) : (
          <MrBubblesFull size={62} className="drop-shadow-sm" />
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="false"
          aria-label="Bubbly — Brix assistant"
          className={cn(
            'fixed z-[69] flex flex-col overflow-hidden rounded-2xl border border-brix-border bg-brix-surface shadow-card-hover',
            'animate-bubbly-in',
            // Mobile: near-fullscreen sheet that clears the launcher + bottom nav.
            'inset-x-3 bottom-[calc(7.5rem+env(safe-area-inset-bottom))] top-16',
            expanded
              ? // Big window: centered, roomy — for reading guides / long answers.
                'sm:inset-x-[max(1.5rem,calc(50%-30rem))] sm:top-[4vh] sm:bottom-[4vh] sm:h-auto sm:max-h-none sm:w-auto'
              : // Desktop: compact docked panel above the launcher.
                'sm:inset-x-auto sm:top-auto sm:right-4 sm:bottom-24 sm:h-[520px] sm:max-h-[80vh] sm:w-[360px]',
          )}
        >
          {/* Header — navy brand strip with the mascot avatar. */}
          <div className="flex items-center justify-between gap-2 bg-brix-primary px-4 py-3 text-white">
            <div className="flex items-center gap-2.5">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/15">
                <MrBubbles mood="happy" size={38} />
              </span>
              <div>
                <p className="text-sm font-semibold leading-tight">Mr. Bubbles</p>
                <p className="text-[11px] leading-tight text-white/70">Your Brix assistant</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                aria-label={expanded ? 'Shrink to the corner panel' : 'Expand to a big window'}
                className="hidden h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-white/80 transition-colors hover:bg-white/15 hover:text-white sm:flex"
              >
                {expanded ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close Bubbly"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-white/80 transition-colors hover:bg-white/15 hover:text-white"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
            <div className={cn('space-y-3', expanded && 'mx-auto h-full w-full max-w-2xl')}>
              {empty ? (
                <EmptyState onPick={(q) => submit(q)} disabled={sending} />
              ) : (
                messages.map((m) => <MessageBubble key={m.id} message={m} />)
              )}
            </div>
          </div>

          {/* Composer */}
          <form
            onSubmit={onSubmit}
            className="bg-brix-surface-alt/40 border-t border-brix-border px-3 py-3"
          >
            <div className={cn('flex items-end gap-2', expanded && 'mx-auto w-full max-w-2xl')}>
              <label htmlFor="bubbly-input" className="sr-only">
                Ask Bubbly a question
              </label>
              <textarea
                id="bubbly-input"
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onInputKeyDown}
                rows={1}
                placeholder="Ask about rentals, troubleshooting, orders…"
                className={cn(
                  'max-h-32 min-h-[40px] flex-1 resize-none rounded-lg border border-brix-border bg-brix-surface px-3 py-2 text-sm',
                  'placeholder:text-brix-muted',
                  'focus-visible:ring-brix-primary/30 focus-visible:border-brix-primary focus-visible:outline-none focus-visible:ring-2',
                )}
              />
              <Button
                type="submit"
                size="icon"
                disabled={sending || input.trim().length === 0}
                aria-label="Send message"
              >
                <Send size={15} />
              </Button>
            </div>
            <p className="mt-1.5 px-1 text-[10px] text-brix-muted">
              Enter to send · Shift+Enter for a new line
            </p>
          </form>
        </div>
      )}
    </div>
  );
}

function EmptyState({ onPick, disabled }: { onPick: (q: string) => void; disabled: boolean }) {
  return (
    <div className="flex h-full flex-col items-center justify-center px-2 text-center">
      <MrBubblesFull size={176} className="mb-2" />
      <p className="text-sm font-semibold text-brix-ink">Hi, I'm Mr. Bubbles! 👋</p>
      <p className="mt-1 max-w-[18rem] text-xs leading-relaxed text-brix-muted">
        Ask me about gas cylinder rentals, soda-machine troubleshooting, ordering &amp; delivery, or
        billing. My answers come from the Brix help library.
      </p>
      <div className="mt-4 grid w-full max-w-[20rem] gap-2">
        {EXAMPLE_QUESTIONS.map((q) => (
          <button
            key={q}
            type="button"
            disabled={disabled}
            onClick={() => onPick(q)}
            className={cn(
              'rounded-lg border border-brix-border bg-brix-surface px-3 py-2 text-left text-xs text-brix-ink',
              'hover:border-brix-primary/40 hover:bg-brix-primary-tint/40 cursor-pointer transition-colors',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user';

  if (!isUser && message.pending) {
    return (
      <div className="flex items-end justify-start gap-2">
        <MrBubbles mood="happy" size={32} className="mb-0.5 shrink-0" />
        <div className="rounded-2xl rounded-bl-sm border border-brix-border bg-brix-surface px-3.5 py-2.5">
          <TypingDots />
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex items-end gap-2', isUser ? 'justify-end' : 'justify-start')}>
      {!isUser && <MrBubbles mood="happy" size={32} className="mb-0.5 shrink-0" />}
      <div
        className={cn(
          'max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed',
          isUser
            ? 'rounded-br-sm bg-brix-primary text-white'
            : 'rounded-bl-sm border border-brix-border bg-brix-surface text-brix-ink',
          message.error && 'border-brix-warning/40 bg-brix-warning/5',
        )}
      >
        <p className="whitespace-pre-wrap break-words">{message.content}</p>
        {!isUser && message.citations && message.citations.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5 border-t border-brix-border pt-2">
            {message.citations.map((c) => (
              <span
                key={c.title}
                className="inline-flex items-center gap-1 rounded-full bg-brix-primary-tint px-2 py-0.5 text-[10px] font-medium text-brix-primary"
              >
                {c.title}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TypingDots() {
  return (
    <span className="flex items-center gap-1" aria-label="Bubbly is typing">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-1.5 w-1.5 animate-bounce rounded-full bg-brix-muted"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </span>
  );
}
